import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { useAppDispatch } from "../../../store/hooks";
import { verifyUserNewEmail } from "../../../features/user/user.thunk";
import { wait } from "../../../helpers";

type TStatus = "pending" | "success" | "error";

export const useNewEmailVerification = () => {
  const [status, setStatus] = useState<TStatus>("pending");
  const [timerCount, setTimerCount] = useState(10);

  const timerRef = useRef<NodeJS.Timeout>();

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const email = searchParams.get("email");
  const token = searchParams.get("token");

  const isValidParams = Boolean(email && token);

  const verifyEmail = useCallback(async () => {
    if (!email || !token) return;

    clearInterval(timerRef.current);
    await wait(2000); // Artificial delay for better UX

    try {
      await dispatch(
        verifyUserNewEmail({ email, emailConfirmedToken: token }),
      ).unwrap();
      setStatus("success");
    } catch {
      setStatus("error");
    } finally {
      timerRef.current = setInterval(() => {
        setTimerCount((prev) => prev - 1);
      }, 1000);
    }
  }, [email, token, dispatch]);

  useEffect(() => {
    if (timerCount <= 0) {
      clearInterval(timerRef.current);
      navigate("/account", { replace: true });
    }
  }, [timerCount, navigate]);

  useEffect(() => {
    void verifyEmail();
    return () => {
      clearInterval(timerRef.current);
    };
  }, [verifyEmail]);

  return { status, timerCount, isValidParams };
};
